'use client';

import { useMemo } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslations, useLocale } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '../model/auth.store';
import { useAuthActions, useRegisterLoading } from '../model/auth.selectors';
import { resolvePostLoginDestination } from '../lib/post-login-destination';
import { isSafeRedirectPath } from '../lib/safe-redirect';
import { getAppleOAuthUrl } from '../lib/apple-oauth';
import { resolveAuthErrorMessage } from '../lib/auth-error-message';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import type { User } from '@/types/user';
import { Input } from '@/shared/ui/shadcn/input';
import { PasswordInput } from '@/shared/ui/PasswordInput';
import { Button } from '@/shared/ui/shadcn/button';
import { Label } from '@/shared/ui/shadcn/label';
import { NoticeBanner } from '@/shared/ui/NoticeBanner';
import { ApiClientError } from '@/shared/lib/api';
import { Loader2 } from 'lucide-react';
import { Logo } from '@/shared/ui/brand/Logo';
import { AppleLogoIcon, GoogleLogoIcon } from './social-icons';
import { APPLE_OAUTH_BUTTON, GOOGLE_OAUTH_BUTTON } from './oauth-button-classes';

// Mirrors the API rule (Laravel Password::default()) so users see the
// requirement before submitting instead of a server round-trip.
const PASSWORD_MIN_LENGTH = 8;

function createRegisterSchema(tValid: ReturnType<typeof useTranslations>) {
  return z.object({
    email: z
      .string()
      .min(1, tValid('required'))
      .email(tValid('email')),
    password: z
      .string()
      .min(
        PASSWORD_MIN_LENGTH,
        tValid('passwordMinLength', { min: PASSWORD_MIN_LENGTH })
      ),
  });
}

type RegisterFormData = z.infer<ReturnType<typeof createRegisterSchema>>;

export function RegisterForm() {
  const t = useTranslations('auth.register');
  const tLogin = useTranslations('auth.login');
  const tValid = useTranslations('common.errors.validation');
  const tErrors = useTranslations('common.errors');
  const errorFallbacks = {
    network: tErrors('network'),
    generic: tErrors('generic'),
  };
  const router = useRouter();
  const searchParams = useSearchParams();
  const currentLocale = useLocale();
  const { register: registerAccount, getGoogleOAuthUrl } = useAuthActions();
  const isRegistering = useRegisterLoading();
  const queryClient = useQueryClient();

  const registerSchema = useMemo(() => createRegisterSchema(tValid), [tValid]);

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: { email: '', password: '' },
  });

  const redirectUrl = isSafeRedirectPath(searchParams.get('redirect'))
    ? searchParams.get('redirect')
    : null;

  const onSubmit = async (data: RegisterFormData) => {
    clearErrors('root');

    try {
      await registerAccount({
        email: data.email,
        password: data.password,
        locale: currentLocale,
      });
      // New email accounts always require code verification (API `verified`
      // middleware blocks everything until the 4-digit code is confirmed).
      const { pendingEmailVerification } = useAuthStore.getState();
      if (pendingEmailVerification) {
        router.push('/auth/verify-email');
        return;
      }
      const user = queryClient.getQueryData<User>(cacheKeys.user) ?? null;
      const destination = await resolvePostLoginDestination(user, {
        queryClient,
        redirectUrl,
      });
      router.push(destination);
    } catch (error) {
      if (error instanceof ApiClientError) {
        const fieldErrors = error.getFieldErrors();
        const keys = Object.keys(fieldErrors);
        if (keys.length > 0) {
          for (const key of keys) {
            if (key === 'email' || key === 'password') {
              setError(key, { message: fieldErrors[key] });
            }
          }
          if (!keys.includes('email') && !keys.includes('password')) {
            const firstKey = keys[0];
            setError('root', {
              message:
                (firstKey ? fieldErrors[firstKey] : undefined) ??
                error.getFirstError(),
            });
          }
        } else {
          setError('root', {
            message: resolveAuthErrorMessage(error, errorFallbacks),
          });
        }
      } else {
        setError('root', {
          message: resolveAuthErrorMessage(error, errorFallbacks),
        });
      }
    }
  };

  const handleGoogleSignUp = async () => {
    try {
      if (redirectUrl) {
        sessionStorage.setItem('oauth_redirect', redirectUrl);
      }
      const oauthUrl = await getGoogleOAuthUrl();
      window.location.href = oauthUrl;
    } catch (error) {
      console.error('Google OAuth error:', error);
      setError('root', {
        message: resolveAuthErrorMessage(error, errorFallbacks),
      });
    }
  };

  const handleAppleSignUp = () => {
    try {
      if (redirectUrl) {
        sessionStorage.setItem('oauth_redirect', redirectUrl);
      }
      window.location.href = getAppleOAuthUrl();
    } catch (error) {
      console.error('Apple OAuth error:', error);
      setError('root', {
        message: resolveAuthErrorMessage(error, errorFallbacks),
      });
    }
  };

  const generalError = errors.root?.message;

  return (
    <div className="flex flex-col items-center w-full">
      <div className="flex justify-center mb-8">
        <Logo />
      </div>
      <div className="w-full space-y-4">
        <h1 className="text-screen-title text-center mb-6">{t('title')}</h1>

        {generalError && (
          <NoticeBanner variant="error" message={generalError} />
        )}

        {/* Email/password form first — users land here to sign up with email
            (mirrors Apple RegisterView ordering; login screen is the inverse). */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">{t('email')}</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              {...register('email')}
              className={errors.email ? 'border-destructive' : ''}
            />
            {errors.email && (
              <p className="text-form-error text-destructive">
                {errors.email.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">{t('password')}</Label>
            <PasswordInput
              id="password"
              autoComplete="new-password"
              {...register('password')}
              className={errors.password ? 'border-destructive' : ''}
            />
            {errors.password && (
              <p className="text-form-error text-destructive">
                {errors.password.message}
              </p>
            )}
          </div>

          <Button
            type="submit"
            disabled={isRegistering}
            className="w-full h-12 rounded-full"
          >
            {isRegistering ? (
              <>
                <Loader2 className="me-2 h-4 w-4 animate-spin" />
                {t('submitting')}
              </>
            ) : (
              t('submit')
            )}
          </Button>
        </form>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-section-eyebrow">
            <span className="bg-background px-2 text-muted-foreground">
              {tLogin('continueWithEmail')}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Button
            type="button"
            className={APPLE_OAUTH_BUTTON}
            onClick={handleAppleSignUp}
          >
            <AppleLogoIcon />
            {tLogin('appleButton')}
          </Button>
          <Button
            type="button"
            className={GOOGLE_OAUTH_BUTTON}
            onClick={handleGoogleSignUp}
          >
            <GoogleLogoIcon />
            {tLogin('googleButton')}
          </Button>
        </div>

        <div className="text-center text-list-item-subtitle text-muted-foreground">
          {t('haveAccount')}{' '}
          <Link
            // Keep the deep-link redirect alive across the login/register switch
            href={
              redirectUrl
                ? `/auth/login?redirect=${encodeURIComponent(redirectUrl)}`
                : '/auth/login'
            }
            className="font-medium text-primary underline hover:text-primary/80"
          >
            {t('signIn')}
          </Link>
        </div>
      </div>
    </div>
  );
}
